/**
 * Group-aware extension of <custom-pagination> for collection pages that
 * group products under category headings (see "Grouping (SEO category
 * headings)" in the "Collection products" section settings).
 *
 * Why this exists instead of just using <custom-pagination> directly:
 * the base class's loadMore() assumes a flat list of ".js-pagination-result"
 * items and simply inserts the newly fetched ones after the last existing
 * item. Grouped collections render each metafield value as its own
 * <div class="product-grid-group" id="group-{{ handle }}"> with its own
 * <ul> of ".cc-grouped-result" items. If page 2 starts partway through a
 * category that already has a heading on page 1, naively appending page 2's
 * markup would create a SECOND heading and list for that same category
 * further down the page.
 *
 * This subclass instead walks each ".product-grid-group" in the newly
 * fetched page and either:
 *   - merges its items into an existing group with the same id (if that
 *     category's heading already exists on the page), or
 *   - appends the whole new group block (heading + list) if it's the first
 *     time that category has appeared.
 *
 * Requires custom-pagination.js to be loaded first (this class extends the
 * "custom-pagination" element it defines) and does not modify that file.
 *
 * Usage (see the "Collection products" section, grouped branch):
 *   <custom-pagination-grouped data-infinite-scroll="{{ settings.pagination_infinite }}"
 *     data-pause-infinite-scroll="false">
 *     <div class="product-grid-group" id="group-trophies">
 *       <h2 class="product-grid-group__heading">Trophies</h2>
 *       <ul><li class="cc-grouped-result">...</li></ul>
 *     </div>
 *     {% render 'pagination', paginate: paginate, style: settings.pagination_style %}
 *   </custom-pagination-grouped>
 */
(function () {
  function defineGroupedPagination() {
    var CustomPaginationBase = customElements.get('custom-pagination');

    if (!CustomPaginationBase) {
      // custom-pagination.js hasn't defined the base class yet. Both
      // scripts load with `defer`, in source order, so this shouldn't
      // normally happen — but bail safely rather than throw if it does.
      console.log('custom-pagination-grouped.js: base "custom-pagination" element not found.'); // eslint-disable-line
      return;
    }

    if (customElements.get('custom-pagination-grouped')) return;

    class CustomPaginationGrouped extends CustomPaginationBase {
      /**
       * Same click-to-abort behavior as the base class, extended to also
       * cover grouped result items (".cc-grouped-result") rather than only
       * the flat-list ".js-pagination-result" items the base class checks.
       * @param {object} evt - Event object
       */
      handleResultClick(evt) {
        var withinResult = evt.target.closest('.js-pagination-result') || evt.target.closest('.cc-grouped-result');
        if (evt.target.closest('a') && withinResult && this.controller) {
          this.controller.abort();
        }
      }

      /**
       * Fetches the next page and merges its grouped results into the
       * existing groups already on the page, creating new group blocks
       * only for categories not already present.
       */
      async loadMore() {
        if (!this.isFetching && this.dataset.pauseInfiniteScroll === 'false') {
          this.isFetching = true;
          this.controller = new AbortController();

          try {
            if (!this.loadMoreButton) throw new Error("No 'Load more' button found.");

            this.loadMoreButton.classList.add('is-loading');
            this.loadMoreButton.setAttribute('aria-disabled', 'true');

            if (this.pagination.dataset.paginationStyle === 'traditional') {
              this.pagination.classList.add('is-loading');
            }

            var response = await fetch(this.loadMoreButton.href, {
              signal: this.controller.signal
            });

            if (!response.ok) throw new Error(response.status);
            if (this.dataset.pauseInfiniteScroll === 'true') {
              throw new Error('Infinite scroll was interrupted.');
            }

            var tmpl = document.createElement('template');
            tmpl.innerHTML = await response.text();

            // Merge each fetched group into the existing group with the
            // same id, or append it as a new group (right before the
            // pagination nav) if none exists yet. Inserting each new group
            // immediately before the (constant) pagination element, in
            // sequence, naturally preserves their original order.
            var newGroups = tmpl.content.querySelectorAll('.product-grid-group');

            newGroups.forEach(function (newGroup) {
              var groupId = newGroup.id;
              var existingGroup = groupId ? this.querySelector('#' + CSS.escape(groupId)) : null;

              if (existingGroup) {
                var existingList = existingGroup.querySelector('ul');
                var newItems = newGroup.querySelectorAll('.cc-grouped-result');
                newItems.forEach(function (item) {
                  existingList.appendChild(item);
                });
              } else if (this.pagination && this.pagination.parentNode) {
                this.pagination.parentNode.insertBefore(newGroup, this.pagination);
              } else {
                this.appendChild(newGroup);
              }
            }, this);

            this.results = this.querySelectorAll('.cc-grouped-result');

            var newPagination = tmpl.content.querySelector('.js-pagination');
            if (newPagination && newPagination.dataset.isMoreResults) {
              this.pagination.innerHTML = newPagination.innerHTML;

              if (this.loadMoreButton) {
                this.loadMoreButton.removeEventListener('click', this.loadMoreClickHandler);
                this.loadMoreButton = this.querySelector('.js-pagination-load-more');
                this.loadMoreButton.addEventListener('click', this.loadMoreClickHandler);
              }
            } else if (this.pagination) {
              this.pagination.remove();
              if (this.intersectingTimer) clearInterval(this.intersectingTimer);
              if (this.paginationObserver) this.paginationObserver.disconnect();
              document.querySelectorAll('.js-when-paginated-only').forEach(function (elem) { elem.remove(); });
            }
          } catch (error) {
            console.log('Unable to fetch next page of grouped results', error); // eslint-disable-line
            if (this.intersectingTimer) clearInterval(this.intersectingTimer);
          } finally {
            this.controller = null;

            if (this.loadMoreButton) {
              this.loadMoreButton.classList.remove('is-loading');
              this.loadMoreButton.removeAttribute('aria-disabled');
            }

            this.isFetching = false;

            if (this.pagination && this.pagination.dataset.paginationStyle === 'traditional') {
              this.pagination.classList.remove('is-loading');
            }
          }
        }
      }
    }

    customElements.define('custom-pagination-grouped', CustomPaginationGrouped);
  }

  defineGroupedPagination();
})();